import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { PermissionService } from '../../core/authorization/permission.service';
import { type NavigationItem } from './navigation.models';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatMenuModule, MatTooltipModule, RouterLink, RouterLinkActive],
  selector: 'app-navigation',
  styleUrl: './navigation.scss',
  templateUrl: './navigation.html',
})
export class AppNavigation {
  private readonly permissionService = inject(PermissionService);
  private readonly router = inject(Router);
  private readonly collapsedGroups = signal<ReadonlySet<string>>(new Set());

  readonly items = input.required<readonly NavigationItem[]>();
  readonly temporary = input(false);
  readonly itemSelected = output<NavigationItem>();
  readonly collapsed = model(false);
  protected readonly desktopCollapsed = computed(() => !this.temporary() && this.collapsed());
  protected readonly visibleItems = computed(() => this.filterVisibleItems(this.items()));

  protected toggleCollapsed(): void {
    if (this.temporary()) {
      return;
    }

    const collapsed = !this.collapsed();
    this.collapsed.set(collapsed);
  }

  protected toggleGroup(groupId: string): void {
    this.collapsedGroups.update((current) => {
      const next = new Set(current);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }

  protected isGroupExpanded(groupId: string): boolean {
    return !this.collapsedGroups().has(groupId);
  }

  protected isGroupActive(item: NavigationItem): boolean {
    return (
      item.children?.some(
        (child) =>
          child.route !== undefined &&
          this.router.isActive(child.route, {
            paths: 'subset',
            queryParams: 'ignored',
            fragment: 'ignored',
            matrixParams: 'ignored',
          }),
      ) ?? false
    );
  }

  protected select(item: NavigationItem): void {
    this.itemSelected.emit(item);
  }

  private filterVisibleItems(items: readonly NavigationItem[]): readonly NavigationItem[] {
    return items.flatMap((item) => {
      if (
        (item.requiredPermission !== undefined && !this.permissionService.hasPermission(item.requiredPermission)) ||
        (item.requiredAnyPermission !== undefined && !item.requiredAnyPermission.some(code => this.permissionService.hasPermission(code)))
      ) {
        return [];
      }

      if (item.children === undefined) {
        return [item];
      }

      const children = this.filterVisibleItems(item.children);
      return children.length === 0 ? [] : [{ ...item, children }];
    });
  }
}
